/**
 * Turning a dropped palette placeholder into a fresh {@link BoardPart}.
 *
 * A SchemePart is a **snapshot of the placeholder** it was created from: the backend persists the save DTO
 * verbatim and never re-reads the placeholder, so the client must copy the placeholder's settings into the
 * part at drop time (facility sub-type, accommodation category, unit patterns, equipment, cost type,
 * visibility, note, transport kind). That copy is a {@link PlaceholderSnapshot} — produced from the fetched
 * placeholder detail ({@link placeholderToSnapshot}), or a minimal {@link fallbackSnapshot} when the detail
 * can't be loaded. The link back to the source is kept via `sourcePlaceholderId`.
 *
 * A client-created part gets a UUIDv7 that is used as BOTH its `uid` and `serverId` — the server accepts
 * client-generated ids, so nothing needs reconciling after a save and the board can stay authoritative.
 */
import type { GetPlaceholderResponse } from '@/api/generated/schemas';
import { PlaceholderCostType } from '@/api/generated/schemas';
import { uuidv7 } from '@/shared/lib/uuidv7';
import { DEFAULT_CURRENCY, DEFAULT_TIMED_HOURS } from '../model';
import type { BoardPart, PalettePayload } from '../model';

/**
 * The subset of a placeholder's settings that is snapshotted onto a new part. Cost *amounts* are not part
 * of a placeholder (only the cost *type* is), so they are always entered per-schema and stay empty here.
 */
export interface PlaceholderSnapshot {
  name: string;
  costType: PlaceholderCostType;
  showInProgram: boolean;
  note: string | null;
  transportKind: BoardPart['transportKind'];
  facilitySubTypeId: number | null;
  facilitySubTypeName: string | null;
  accommodationCategoryCbId: number | null;
  accommodationCategoryName: string | null;
  unitPatterns: BoardPart['unitPatterns'];
  equipmentIds: number[];
}

/** Map a fetched placeholder detail to the snapshot copied onto a new part. */
export function placeholderToSnapshot(
  detail: GetPlaceholderResponse,
  fallbackName: string
): PlaceholderSnapshot {
  return {
    name: detail.displayName ?? detail.name ?? fallbackName,
    costType: detail.costType,
    showInProgram: detail.showInProgram,
    note: detail.note ?? null,
    transportKind: detail.transportKind ?? null,
    facilitySubTypeId: detail.facilitySubTypeId ?? null,
    facilitySubTypeName: detail.facilitySubTypeName ?? null,
    accommodationCategoryCbId: detail.accommodationCategoryCbId ?? null,
    accommodationCategoryName: detail.accommodationCategoryName ?? null,
    unitPatterns: detail.unitPatterns ?? [],
    equipmentIds: detail.equipment?.map((e) => e.id) ?? [],
  };
}

/** Minimal snapshot used when the placeholder detail can't be loaded — carries only what the drag payload has. */
export function fallbackSnapshot(payload: PalettePayload): PlaceholderSnapshot {
  return {
    name: payload.name,
    costType: PlaceholderCostType.Calculation,
    showInProgram: true,
    note: null,
    transportKind: payload.transportKind,
    facilitySubTypeId: null,
    facilitySubTypeName: null,
    accommodationCategoryCbId: null,
    accommodationCategoryName: null,
    unitPatterns: [],
    equipmentIds: [],
  };
}

/** New timed part (single day, positioned at `startHour`), snapshotting the placeholder's settings. */
export function newTimedPart(
  payload: PalettePayload,
  dayIdx: number,
  startHour: number,
  snapshot: PlaceholderSnapshot
): BoardPart {
  const id = uuidv7();
  return {
    uid: id,
    serverId: id,
    kind: 'timed',
    type: payload.type,
    transportKind: snapshot.transportKind,
    costType: snapshot.costType,
    name: snapshot.name,
    showInProgram: snapshot.showInProgram,
    note: snapshot.note,
    displayOrder: 0,
    day: dayIdx,
    span: 1,
    row: null,
    startHour,
    hours: DEFAULT_TIMED_HOURS,
    costPax: null,
    costGroup: null,
    currency: DEFAULT_CURRENCY,
    facilitySubTypeId: snapshot.facilitySubTypeId,
    facilitySubTypeName: snapshot.facilitySubTypeName,
    accommodationCategoryCbId: snapshot.accommodationCategoryCbId,
    accommodationCategoryName: snapshot.accommodationCategoryName,
    unitPatterns: snapshot.unitPatterns,
    equipmentIds: snapshot.equipmentIds,
    sourcePlaceholderId: payload.placeholderId,
    altCostItems: [],
  };
}

/**
 * New spanning part — an accommodation booking (nights) or a multi-day service. Starts one unit wide and
 * snapshots the placeholder's settings.
 */
export function newSpanPart(
  payload: PalettePayload,
  kind: 'accommodation' | 'service',
  dayIdx: number,
  row: number | null,
  snapshot: PlaceholderSnapshot
): BoardPart {
  const id = uuidv7();
  return {
    uid: id,
    serverId: id,
    kind,
    type: payload.type,
    transportKind: snapshot.transportKind,
    costType: snapshot.costType,
    name: snapshot.name,
    showInProgram: snapshot.showInProgram,
    note: snapshot.note,
    displayOrder: 0,
    day: dayIdx,
    span: 1,
    row,
    startHour: null,
    hours: DEFAULT_TIMED_HOURS,
    costPax: null,
    costGroup: null,
    currency: DEFAULT_CURRENCY,
    facilitySubTypeId: snapshot.facilitySubTypeId,
    facilitySubTypeName: snapshot.facilitySubTypeName,
    accommodationCategoryCbId: snapshot.accommodationCategoryCbId,
    accommodationCategoryName: snapshot.accommodationCategoryName,
    unitPatterns: snapshot.unitPatterns,
    equipmentIds: snapshot.equipmentIds,
    sourcePlaceholderId: payload.placeholderId,
    altCostItems: [],
  };
}
